// Synoptique Constants - Extracted for better code organization

// Couleurs des ports de sortie des processeurs (32 ports max)
export const PORT_COLORS = [
  "hsl(0, 85%, 55%)",      // Rouge vif - Port 1
  "hsl(200, 95%, 50%)",    // Bleu cyan - Port 2
  "hsl(120, 75%, 45%)",    // Vert - Port 3
  "hsl(45, 100%, 50%)",    // Orange - Port 4
  "hsl(280, 85%, 60%)",    // Violet - Port 5
  "hsl(180, 80%, 45%)",    // Cyan - Port 6
  "hsl(330, 90%, 55%)",    // Rose - Port 7
  "hsl(60, 90%, 50%)",     // Jaune - Port 8
  "hsl(15, 90%, 55%)",     // Orange foncé - Port 9
  "hsl(160, 80%, 45%)",    // Vert menthe - Port 10
  "hsl(260, 85%, 60%)",    // Violet clair - Port 11
  "hsl(30, 100%, 50%)",    // Orange vif - Port 12
  "hsl(300, 80%, 50%)",    // Magenta - Port 13
  "hsl(90, 75%, 45%)",     // Vert lime - Port 14
  "hsl(220, 90%, 55%)",    // Bleu royal - Port 15
  "hsl(350, 85%, 55%)",    // Rouge rosé - Port 16
  "hsl(140, 80%, 45%)",    // Vert émeraude - Port 17
  "hsl(270, 75%, 55%)",    // Pourpre - Port 18
  "hsl(40, 95%, 50%)",     // Ambre - Port 19
  "hsl(190, 85%, 50%)",    // Turquoise - Port 20
  "hsl(320, 80%, 55%)",    // Fuchsia - Port 21
  "hsl(75, 80%, 45%)",     // Chartreuse - Port 22
  "hsl(210, 85%, 55%)",    // Bleu ciel - Port 23
  "hsl(10, 90%, 55%)",     // Vermillon - Port 24
  "hsl(150, 75%, 45%)",    // Jade - Port 25
  "hsl(290, 80%, 55%)",    // Orchidée - Port 26
  "hsl(55, 95%, 50%)",     // Citron - Port 27
  "hsl(70, 100%, 55%)",    // Chartreuse - Port 28
  "hsl(170, 100%, 45%)",   // Vert émeraude - Port 29
  "hsl(330, 100%, 65%)",   // Magenta - Port 30
  "hsl(240, 100%, 60%)",   // Indigo - Port 31
  "hsl(50, 100%, 55%)",    // Or - Port 32
] as const

// Couleurs des prises d'alimentation (32 prises max)
export const OUTLET_COLORS = [
  "hsl(0, 85%, 55%)",      // Rouge vif - Prise 1
  "hsl(200, 95%, 50%)",    // Bleu cyan - Prise 2
  "hsl(120, 75%, 45%)",    // Vert - Prise 3
  "hsl(45, 100%, 50%)",    // Orange - Prise 4
  "hsl(280, 85%, 60%)",    // Violet - Prise 5
  "hsl(180, 80%, 45%)",    // Cyan - Prise 6
  "hsl(330, 90%, 55%)",    // Rose - Prise 7
  "hsl(60, 90%, 50%)",     // Jaune - Prise 8
  "hsl(15, 90%, 55%)",     // Orange foncé - Prise 9
  "hsl(160, 80%, 45%)",    // Vert menthe - Prise 10
  "hsl(260, 85%, 60%)",    // Violet clair - Prise 11
  "hsl(30, 100%, 50%)",    // Orange vif - Prise 12
  "hsl(300, 80%, 50%)",    // Magenta - Prise 13
  "hsl(90, 75%, 45%)",     // Vert lime - Prise 14
  "hsl(220, 90%, 55%)",    // Bleu royal - Prise 15
  "hsl(350, 85%, 55%)",    // Rouge rosé - Prise 16
  "hsl(140, 80%, 45%)",    // Vert émeraude - Prise 17
  "hsl(270, 75%, 55%)",    // Pourpre - Prise 18
  "hsl(40, 95%, 50%)",     // Ambre - Prise 19
  "hsl(190, 85%, 50%)",    // Turquoise - Prise 20
  "hsl(320, 80%, 55%)",    // Fuchsia - Prise 21
  "hsl(75, 80%, 45%)",     // Chartreuse - Prise 22
  "hsl(210, 85%, 55%)",    // Bleu ciel - Prise 23
  "hsl(10, 90%, 55%)",     // Vermillon - Prise 24
  "hsl(150, 75%, 45%)",    // Jade - Prise 25
  "hsl(290, 80%, 55%)",    // Orchidée - Prise 26
  "hsl(55, 95%, 50%)",     // Citron - Prise 27
  "hsl(70, 100%, 55%)",    // Chartreuse - Prise 28
  "hsl(170, 100%, 45%)",   // Vert émeraude - Prise 29
  "hsl(330, 100%, 65%)",   // Magenta - Prise 30
  "hsl(240, 100%, 60%)",   // Indigo - Prise 31
  "hsl(50, 100%, 55%)",    // Or - Prise 32
] as const

// Maximum de panneaux en daisy chain (limite technique des processeurs LED)
export const MAX_DAISY_CHAIN_PANELS = 17

// Configuration par défaut pour le calcul de capacité des ports
export interface PortCapacityConfig {
  colorDepth: 8 | 10 | 12 | 16
  refreshRate: 50 | 60
  hdrEnabled: boolean
}

export const DEFAULT_PORT_CAPACITY_CONFIG: PortCapacityConfig = {
  colorDepth: 8,
  refreshRate: 60,
  hdrEnabled: false
}

/**
 * Calcule la capacité maximale en pixels pour un port RJ45 1G.
 * 
 * Sources:
 * - Linsn LED: https://www.linsnled.com/led-display-connection.html
 * - NovaStar COEX: https://coex.novastar.wiki/en/Intro&Info/Loading-Capacity-Instructions
 */
export function calculatePortCapacity(config: PortCapacityConfig = DEFAULT_PORT_CAPACITY_CONFIG): number {
  const BASE_8BIT_60HZ_CAPACITY = 650_000
  const colorDepthCapacityFactor: Record<PortCapacityConfig['colorDepth'], number> = {
    8: 1,
    10: 0.7,
    12: 0.5,
    16: 0.5,
  }
  const frameRateFactor = 60 / config.refreshRate
  const hdrCapacityFactor = config.hdrEnabled ? 0.5 : 1
  const capacity = BASE_8BIT_60HZ_CAPACITY * colorDepthCapacityFactor[config.colorDepth] * frameRateFactor * hdrCapacityFactor
  
  return Math.round(capacity)
}

/**
 * Calcule le nombre maximum de panneaux pouvant être connectés sur un port
 * en fonction de la résolution des panneaux et de la configuration
 */
export function calculateMaxPanelsPerPort(
  panelResolution: { width: number; height: number },
  config: PortCapacityConfig = DEFAULT_PORT_CAPACITY_CONFIG
): number {
  const portCapacity = calculatePortCapacity(config)
  const panelPixels = panelResolution.width * panelResolution.height
  
  if (panelPixels === 0) return MAX_DAISY_CHAIN_PANELS
  
  const maxPanels = Math.floor(portCapacity / panelPixels)
  
  return Math.max(1, maxPanels)
}

// Default viewBox settings
export const DEFAULT_VIEWBOX = {
  x: -430,
  y: -300,
  width: 1146,  // 10000 / 8.73
  height: 1146
} as const

// Reference zoom level (873% = 100%)
export const REFERENCE_ZOOM = 8.73

// Helper functions for colors
export function getPortColor(portNumber: number): string {
  const index = (portNumber - 1) % PORT_COLORS.length
  return PORT_COLORS[index >= 0 ? index : 0]
}

export function getOutletColor(outletNumber: number): string {
  const index = (outletNumber - 1) % OUTLET_COLORS.length
  return OUTLET_COLORS[index >= 0 ? index : 0]
}

// Helper functions for formatting
export function formatResolution(res: { width: number; height: number } | string): string {
  if (typeof res === 'string') return res
  return `${res.width}×${res.height}`
}

export function formatNumber(num: number | string): string {
  if (typeof num === 'string') return num
  return num.toLocaleString()
}
